import { type ReactNode, useId } from "react";
import { type ITooltip, Tooltip } from "react-tooltip";
import { Portal } from "../Portal";
import { cn } from "../../utils/tailwind";

interface CustomTooltipWrapperProps {
  content?: string | null;
  html?: string | null;
  jsx?: ReactNode | null;
  children: ReactNode;
  place?: ITooltip["place"];
  testid?: string;
  positionStrategy?: ITooltip["positionStrategy"];
  className?: string;
}

export const ToolTipBase: React.FC<
  { id: string; children?: ReactNode | null } & Pick<CustomTooltipWrapperProps, "positionStrategy" | "place">
> = ({ id, positionStrategy = "absolute", place, children }) => (
  <Portal>
    <Tooltip
      id={id}
      place={place}
      className="max-w-[20rem] whitespace-pre-wrap normal-case dark:bg-gray-100 dark:text-gray-800"
      style={{ zIndex: Number.MAX_SAFE_INTEGER }}
      positionStrategy={positionStrategy}>
      {children}
    </Tooltip>
  </Portal>
);

const CustomTooltipWrapper = ({
  content,
  html,
  jsx,
  children,
  place,
  testid,
  positionStrategy,
  className,
}: CustomTooltipWrapperProps) => {
  const toolTipId = useId();

  if (typeof content === "string" || typeof html === "string") {
    return (
      <>
        <ToolTipBase id={toolTipId} place={place} positionStrategy={positionStrategy} />
        <span
          data-tooltip-id={toolTipId}
          data-tooltip-content={content || undefined}
          data-tooltip-html={html || undefined}
          className={cn("hover:cursor-pointer", className)}
          data-testid={testid}>
          {/*
           * Putting every Element that should have a tooltip into a span can is not optimal
           * for many elements this will breaks their ui. Todo look for a less invasive way.
           */}
          {children}
        </span>
      </>
    );
  }
  if (jsx) {
    return (
      <>
        <ToolTipBase id={toolTipId} place={place} positionStrategy={positionStrategy}>
          {jsx}
        </ToolTipBase>
        <span data-tooltip-id={toolTipId}>{children}</span>
      </>
    );
  }

  return <>{children}</>;
};

export default CustomTooltipWrapper;
